import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Clock, Headphones, ImagePlus, Mail, RefreshCw, Send, ShieldCheck, X, Zap } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import SupportAgentPicker, { getAgentProfile } from "../components/SupportAgentPicker";
import { siteConfig, storageKey } from "../lib/siteConfig";
import ImagePreviewModal from "../components/ImagePreviewModal";
import LinkifiedText from "../components/LinkifiedText";
import { readChatImage } from "../lib/imageUpload";
import ChatImageAttachment from "../components/ChatImageAttachment";

const guestKey = storageKey("guest_chat_id");
const supportEmail = siteConfig.supportEmail;
const initialMessageCount = 5;
const messageLoadStep = 8;

function getGuestId() {
  let id = localStorage.getItem(guestKey);
  if (!id) {
    id = crypto?.randomUUID?.() || `guest-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    localStorage.setItem(guestKey, id);
  }
  return id;
}

export default function Support() {
  const nav = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const isAuthed = user && user !== false;
  const [guestId] = useState(getGuestId);
  const [chatTicket, setChatTicket] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [visibleMessageCount, setVisibleMessageCount] = useState(initialMessageCount);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const scrollerRef = useRef(null);
  const fileRef = useRef(null);
  const messageRef = useRef(null);
  const selectedProfile = getAgentProfile(selectedAgent);

  const loadChat = useCallback(async () => {
    if (authLoading) return;
    try {
      if (isAuthed) {
        const { data } = await api.get("/support/my");
        setChatTicket(data.find((t) => t.subject === "Live agent chat") || null);
        return;
      }
      const { data } = await api.get(`/support/chat/${guestId}`);
      setChatTicket(data?.id ? data : null);
    } catch {
      setChatTicket((current) => current);
    }
  }, [authLoading, guestId, isAuthed]);

  useEffect(() => { loadChat(); }, [loadChat]);

  useEffect(() => {
    if (authLoading) return undefined;
    const timer = setInterval(() => {
      if (document.visibilityState !== "hidden") loadChat();
    }, 8000);
    return () => clearInterval(timer);
  }, [authLoading, loadChat]);

  const goBack = useCallback(() => {
    const from = location.state?.from;
    if (typeof from === "string" && from && from !== location.pathname) {
      nav(from, { replace: true });
      return;
    }
    if (location.key && location.key !== "default") {
      nav(-1);
      return;
    }
    nav(isAuthed ? "/dashboard" : "/", { replace: true });
  }, [isAuthed, location.key, location.pathname, location.state, nav]);

  const selectImage = async (file) => {
    if (!file) return;
    try {
      setErr("");
      setAttachment(await readImage(file));
    } catch (e) {
      setErr(e.message);
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const text = message.trim();
    const picked = attachment;
    if (!text && !picked) return;
    if (!selectedProfile) {
      setErr("Choose DRACO, SOPHIA, or AURUM first.");
      return;
    }
    setErr("");
    setLoading(true);
    const temp = {
      id: `local-${Date.now()}`,
      sender: "user",
      message: text,
      agent_target: selectedProfile?.name,
      image_url: picked?.dataUrl,
      image_name: picked?.name,
      created_at: new Date().toISOString(),
    };
    setChatTicket((current) => appendLocalMessage(current, temp, {
      name: isAuthed ? user.username : "Guest",
      email: isAuthed ? user.email : undefined,
      guestId,
    }));
    setMessage("");
    setAttachment(null);
    try {
      const { data } = await api.post("/support/chat", {
        guest_id: isAuthed ? undefined : guestId,
        name: isAuthed ? user.username : "Guest",
        email: isAuthed ? user.email : undefined,
        message: text,
        agent_target: selectedProfile?.name,
        image_url: picked?.dataUrl,
        image_name: picked?.name,
      });
      setChatTicket(data);
      setTimeout(loadChat, 150);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const messages = useMemo(() => scopedAgentMessages(chatTicket, selectedProfile), [chatTicket, selectedProfile]);
  const hiddenMessageCount = Math.max(0, messages.length - visibleMessageCount);
  const visibleMessages = useMemo(
    () => messages.slice(Math.max(0, messages.length - visibleMessageCount)),
    [messages, visibleMessageCount],
  );

  const pickAgent = (agent) => {
    setSelectedAgent(agent);
    setErr("");
    window.setTimeout(() => messageRef.current?.focus(), 0);
  };

  useEffect(() => {
    setVisibleMessageCount(initialMessageCount);
  }, [selectedProfile?.name]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [chatTicket?.last_message_at, messages.length]);

  return (
    <div className="support-page min-h-[100dvh] bg-[#07100b] text-white">
      <header className="sticky top-0 z-30 bg-[#07100b]/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={goBack} className="p-2 rounded-full hover:bg-white/10" aria-label="Back">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <img src="/sophia-favicon-64.png" alt="" className="w-10 h-10 rounded-full object-cover ring-1 ring-[#D4AF37]/60" />
            <div className="min-w-0">
              <h1 className="font-display text-lg font-bold truncate">{siteConfig.name} Support</h1>
              <p className="text-xs text-emerald-300">Online queue</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={loadChat} className="p-2 rounded-full hover:bg-white/10" aria-label="Refresh">
              <RefreshCw className="w-5 h-5" />
            </button>
            <a href={`mailto:${supportEmail}`} className="p-2 rounded-full hover:bg-white/10" aria-label="Email support">
              <Mail className="w-5 h-5" />
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto w-full px-0 md:px-8 py-0 md:py-5 grid lg:grid-cols-[320px_minmax(0,1fr)] gap-0 md:gap-5">
        <aside className="hidden lg:block space-y-3">
          <div className="rounded-2xl border border-[#D4AF37]/25 bg-[#0A130E] p-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 grid place-items-center">
                <Headphones className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <p className="font-semibold">Support desk</p>
                <p className="text-xs text-white/45">Replies stay in this app</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <Info icon={Zap} label="Fast" />
              <Info icon={ShieldCheck} label="Secure" />
              <Info icon={Clock} label="24/7" />
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#0A130E] p-3 space-y-2">
            {["Load money", "Redeem help", "Game login", "Account issue"].map((text) => (
              <button
                key={text}
                onClick={() => setMessage(text)}
                className="w-full flex items-center justify-between rounded-xl px-3 py-3 bg-white/[0.03] hover:bg-white/[0.06] border border-white/5 text-sm"
              >
                <span>{text}</span>
                <CheckCircle2 className="w-4 h-4 text-[#D4AF37]" />
              </button>
            ))}
          </div>

          <Link to="/" className="hidden lg:block text-center text-sm text-white/45 hover:text-white py-2">
            Back to {siteConfig.name}
          </Link>
        </aside>

        <section className="bg-[#07100b] overflow-hidden min-h-[calc(100dvh-4rem)] md:min-h-[calc(100vh-150px)] flex flex-col md:rounded-2xl md:border md:border-[#D4AF37]/25">
          <div className="px-4 py-3 border-b border-white/10 bg-[#0A130E] flex items-center justify-between">
            <div>
              <p className="font-semibold">Agent chat</p>
              <p className="text-xs text-white/45">{chatTicket?.status ? chatTicket.status.replace("_", " ") : "Ready"}</p>
            </div>
            <span className="inline-flex items-center gap-1.5 text-xs text-emerald-300">
              <span className="w-2 h-2 rounded-full bg-emerald-400" /> Active
            </span>
          </div>

          <div className="border-b border-white/10 bg-[#07100b] p-3">
            <SupportAgentPicker selectedAgent={selectedAgent} onPick={pickAgent} />
          </div>

          <div ref={scrollerRef} className="flex-1 overflow-y-auto px-4 py-5 space-y-3 bg-[radial-gradient(circle_at_top,_rgba(212,175,55,0.08),_transparent_36%),#07100b]">
            {hiddenMessageCount > 0 && (
              <div className="flex justify-center pb-1">
                <button
                  type="button"
                  onClick={() => setVisibleMessageCount((count) => Math.min(messages.length, count + messageLoadStep))}
                  className="rounded-full border border-white/10 bg-black/30 px-3 py-1.5 text-xs font-semibold text-white/60 hover:border-[#D4AF37]/50 hover:text-white"
                >
                  Load earlier messages ({hiddenMessageCount})
                </button>
              </div>
            )}
            {visibleMessages.map((msg) => <ChatBubble key={msg.id || `${msg.sender}-${msg.created_at}`} msg={msg} onImagePreview={setImagePreview} />)}
            {err && (
              <div className="text-sm text-red-300 bg-red-900/20 border border-red-800/40 rounded-lg p-3">
                {err}
              </div>
            )}
          </div>

          <form onSubmit={submit} className="p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] md:p-4 bg-[#0A130E] border-t border-white/10">
            {attachment && (
              <div className="mb-2 flex items-center gap-2 rounded-xl border border-white/10 bg-black/30 p-2">
                <img src={attachment.dataUrl} alt="" className="w-10 h-10 rounded-lg object-cover" />
                <span className="min-w-0 flex-1 truncate text-xs text-white/70">{attachment.name}</span>
                <button type="button" onClick={() => setAttachment(null)} className="p-1.5 rounded-full hover:bg-white/10" aria-label="Remove image">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
            <div className="flex items-end gap-2">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => selectImage(e.target.files?.[0])} />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                title="Add image"
                aria-label="Add image"
                className="w-12 h-12 rounded-full bg-white/[0.06] border border-white/10 text-white grid place-items-center hover:bg-white/[0.1] shrink-0"
              >
                <ImagePlus className="w-4 h-4" />
              </button>
              <textarea
                ref={messageRef}
                rows={1}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    submit(e);
                  }
                }}
                placeholder={selectedProfile ? `Message ${selectedProfile.name}...` : "Choose an agent first..."}
                className="min-h-[48px] max-h-32 flex-1 resize-none rounded-2xl bg-black/40 border border-white/10 px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none"
              />
              <button
                type="submit"
                disabled={!selectedProfile || loading || (!message.trim() && !attachment)}
                className="w-12 h-12 rounded-full bg-[#D4AF37] text-black grid place-items-center hover:bg-[#F1C40F] disabled:opacity-50 shrink-0"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </form>
        </section>
      </main>
      <ImagePreviewModal image={imagePreview} onClose={() => setImagePreview(null)} />
    </div>
  );
}

function Info({ icon: Icon, label }) {
  return (
    <div className="rounded-xl bg-white/[0.03] border border-white/5 py-3">
      <Icon className="w-4 h-4 mx-auto text-[#D4AF37] mb-1" />
      <div className="text-[11px] text-white/55">{label}</div>
    </div>
  );
}

function normalizeMessages(ticket) {
  if (!ticket) return [];
  if (Array.isArray(ticket.messages) && ticket.messages.length) return ticket.messages;
  const out = [];
  if (ticket.message) out.push({ id: `${ticket.id}-user`, sender: "user", message: ticket.message, created_at: ticket.created_at });
  if (ticket.admin_reply) out.push({ id: `${ticket.id}-agent`, sender: "agent", message: ticket.admin_reply, created_at: ticket.updated_at || ticket.created_at });
  return out;
}

function scopedAgentMessages(ticket, selectedProfile) {
  const ticketMessages = normalizeMessages(ticket);
  if (!selectedProfile) {
    return [
      {
        id: "welcome-choose-agent",
        sender: "agent",
        message: "Choose DRACO, SOPHIA, or AURUM to start a personal chat.",
        created_at: "",
      },
      ...ticketMessages.filter((msg) => !getAgentProfile(msg.agent_target)),
    ];
  }
  const agentName = selectedProfile.name;
  return [
    {
      id: `welcome-${agentName}`,
      sender: "agent",
      agent_target: agentName,
      message: `You're now messaging ${agentName}.`,
      created_at: "",
    },
    ...ticketMessages.filter((msg) => getAgentProfile(msg.agent_target)?.name === agentName),
  ];
}

function ChatBubble({ msg, onImagePreview }) {
  const isAgent = msg.sender === "agent";
  const target = getAgentProfile(msg.agent_target)?.name;
  return (
    <div className={`flex ${isAgent ? "justify-start" : "justify-end"}`}>
      <div className={`max-w-[88%] sm:max-w-[82%] rounded-2xl px-3 py-2 text-sm border shadow-sm ${
        isAgent
          ? "bg-white/[0.07] border-white/10 text-white rounded-bl-md"
          : "bg-[#D4AF37] border-[#D4AF37] text-black rounded-br-md"
      }`}>
        <div className={`text-[10px] mb-1 ${isAgent ? "text-white/45" : "text-black/55"}`}>
          {isAgent ? target || "Agent" : "You"}
        </div>
        {msg.image_url && (
          <ChatImageAttachment
            src={msg.image_url}
            name={msg.image_name || "Attachment"}
            onPreview={onImagePreview}
            className="block max-h-44 max-w-full rounded-xl object-contain sm:max-h-56"
          />
        )}
        {msg.message && <LinkifiedText text={msg.message} linkClassName={isAgent ? "text-[#F8E08E]" : "text-black"} />}
      </div>
    </div>
  );
}

function appendLocalMessage(ticket, msg, fallback) {
  const messages = [...normalizeMessages(ticket), msg];
  return {
    ...(ticket || {}),
    id: ticket?.id || `local-${fallback.guestId || Date.now()}`,
    name: ticket?.name || fallback.name || "Guest",
    email: ticket?.email || fallback.email || `${fallback.guestId || "guest"}@${siteConfig.guestEmailDomain}`,
    subject: "Live agent chat",
    status: "open",
    message: msg.message || "[Image]",
    messages,
    last_message_at: msg.created_at,
  };
}

function readImage(file) {
  return readChatImage(file);
}
